import PropTypes from 'prop-types';
import React from 'react';
import { Icon, Table, DatePicker, Typography, Tag, Tooltip } from 'antd';
import moment from 'moment';
import { withTranslation } from 'react-i18next';

import { LoadingIcon } from '../../../components/loading';
import money from '../../../helpers/money';
import { getTransactionFromUri } from '../../../helpers/transaction-utils';

const getTransactionType = (uri) => ((uri.includes('internal')) ? 'internal' : 'external');

const columns = (isStatic, showBankAccount = false, t) => [
  {
    title: t('type'),
    dataIndex: 'dst_uri',
    filters: [
      {
        text: t('transaction.type.internal'),
        value: 'internal',
      },
      {
        text: t('transaction.type.external'),
        value: 'external',
      },
    ],
    onFilter: (value, { dst_uri }) => (value === (getTransactionType(dst_uri) || 'internal')),
    width: 100,
    render: (uri) => t(`transaction.type.${getTransactionType(uri) || 'internal'}`),
  }, {
    title: t('created_at'),
    dataIndex: 'created_at',
    sorter: !isStatic && (({ created_at: createdAt1 }, { created_at: createdAt2 }) => ((createdAt1 < createdAt2) ? (-1) : (1))),
    defaultSortOrder: 'descend',
    filterIcon: (filtered) => <Icon type="filter" style={{ color: (filtered) ? '#1890ff' : undefined }} />,
    filterDropdown: (isStatic)
      ? (null)
      : (({ setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            mode={['date', 'date']}
            onChange={(dates, dateStrings) => {
              if (!dates.length) {
                clearFilters();
              } else {
                setSelectedKeys(dateStrings);
              }

              confirm();
            }}
          />
        </div>
      )),
    render: (createdAt) => {
      if (!createdAt || typeof createdAt === 'undefined') {
        return '';
      }
      const createdAtToDate = new Date(createdAt);
      const diff = moment(createdAtToDate).diff(moment(), 'days');
      const dateString = (diff >= 0) ? moment(createdAtToDate).fromNow() : moment(createdAtToDate).format('lll');

      return <Tooltip placement="topLeft" title={createdAtToDate.toLocaleString()}>{dateString}</Tooltip>;
    },
  },
  {
    title: t('amount'),
    dataIndex: 'amount',
    render: (_, record) => {
      const amount = money.toDisplay(record.amount, record.currency);
      let color = '#4b998d';
      let lineThrough = false;
      let badge = '';

      if (record.debit) {
        color = 'rgb(0, 0, 0, 0.65)';
      }

      if (['cancel', 'canceled'].includes(record.state)) {
        lineThrough = true;
        color = '#ccc';
        badge = <Tag>canceled</Tag>;
      }

      if (['queued'].includes(record.state)) {
        color = '#ccc';
        badge = <Tag>pending</Tag>;
      }

      return (
        <>
          <Typography.Text style={{ color, textDecoration: (lineThrough) ? 'line-through' : '' }}>
            {(record.debit) ? '-' : ''}{amount}
          </Typography.Text>
          {badge}
        </>
      );
    },
  },
  {
    title: t('name'),
    dataIndex: 'name',
  },
  {
    width: (showBankAccount) ? null : 0,
    title: (showBankAccount) ? t('bank_account') : null,
    key: 'bank_account',
    dataIndex: 'dst_uri',
    render: (value) => {
      try {
        return getTransactionFromUri(value).accountNumber;
      } catch {
        return '';
      }
    },
  },
  {
    title: t('description'),
    dataIndex: 'description',
  },
];

const TransactionTable = ({ showBankAccount, ...props }) => (
  <Table
    {...props}
    loading={{
      indicator: LoadingIcon,
      size: 'large',
      spinning: props.loading || false,
    }}
    rowKey={({ id }, index) => (id || index)}
    columns={columns(props.static || false, showBankAccount, props.t)}
    dataSource={props.transactions}
    pagination={false}
    size="small"
    scroll={{ x: true }}
  />
);

TransactionTable.propTypes = {
  loading: PropTypes.bool,
  static: PropTypes.bool,
  t: PropTypes.func,
  transactions: PropTypes.any,
  showBankAccount: PropTypes.bool,
};

export default withTranslation()(TransactionTable);
